import React from 'react';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { APIs } from '../../constants/endPointsURLs';
import InputField from '../common/InputField/InputField';
import Button from '../common/Button/Button';
import errorMessages from './errorMessages';
import config from '../../dashboard_config.json';
import { fetchUserCountry, getUserCountryFromLocalStorage } from '../GuestMode/form/utils/userCountryUtils';

class ResetPasswordWizard extends React.Component {
    state = {
        step: 'DISCOVERY', // DISCOVERY, IDENTITY, OTP, NEW_PASS
        validationType: '', // EMAIL or SMS
        username: '',
        email: '',
        phoneNumber: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
        showNewPassword: false,
        showConfirmPassword: false,
        isSubmitting: false,
        isErrorMessageDisplayed: false,
        currentErrorMessage: '',
        resendTimer: 0,
        resendAttempts: 0,
        isBanned: false,
        otpFailures: 0,
        isLockedOut: false,
        countryCode: 'us',
    };

    timerInterval = null;
    banRefreshTimer = null;
    lockoutRefreshTimer = null;

    componentDidMount() {
        this.handleDiscovery();
        this.checkSecurityStatus();
        this.initCountry();
    }

    initCountry = async (type = this.state.validationType) => {
        const key = config.dashboard.guests.guestLocation.localStorageKeyName;
        const storedCountry = localStorage.getItem(key);

        if (storedCountry) {
            this.setState({ countryCode: storedCountry });
        } else if (type === 'SMS') {
            const fetchedCountry = await fetchUserCountry();
            if (fetchedCountry) {
                this.setState({ countryCode: fetchedCountry });
            }
        } else {
            // Default fallback if no SMS and no storage
            this.setState({ countryCode: config.dashboard.guests.guestLocation.fallBackCountryCode });
        }
    };

    componentWillUnmount() {
        if (this.timerInterval) clearInterval(this.timerInterval);
        if (this.banRefreshTimer) clearTimeout(this.banRefreshTimer);
        if (this.lockoutRefreshTimer) clearTimeout(this.lockoutRefreshTimer);
    }

    handleDiscovery = () => {
        this.setState({ isSubmitting: true, isErrorMessageDisplayed: false });
        axios({
            method: 'get',
            url: APIs.otpValidationType,
            withCredentials: true,
        })
            .then((response) => {
                const type = (response.data.otpValidationType || response.data.type || response.data.status || 'EMAIL').toUpperCase();
                const validationType = type === 'PHONE' ? 'SMS' : type;
                this.setState({
                    validationType,
                    step: 'IDENTITY',
                    isSubmitting: false
                });
                this.initCountry(validationType);
            })
            .catch((err) => {
                console.error("Discovery Error:", err);
                this.setState({
                    isErrorMessageDisplayed: false, // Don't block UI on discovery failure
                    isSubmitting: false,
                    validationType: 'EMAIL', // Default to EMAIL on fail
                    step: 'IDENTITY'
                });
            });
    };

    handleIdentitySubmit = () => {
        const { validationType, email, phoneNumber, username } = this.state;
        this.setState({ isSubmitting: true, isErrorMessageDisplayed: false });

        axios({
            method: 'post',
            url: APIs.resetPassHandler,
            data: {
                username,
                [validationType === 'EMAIL' ? 'email' : 'phone']: validationType === 'EMAIL' ? email : (phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`),
            },
            withCredentials: true,
        })
            .then((response) => {
                if (response.status === 200) {
                    this.setState({ step: 'OTP', isSubmitting: false });
                    this.startResendTimer();
                    this.checkSecurityStatus();
                } else {
                    this.showError(response.data.message || errorMessages.serverErr);
                }
            })
            .catch((err) => {
                console.error("API Error:", err);
                const serverMsg = this.extractErrorMessage(err);
                this.showError(serverMsg || errorMessages.serverNoResponse);
            });
    };

    handleOTPSubmit = () => {
        const { validationType, email, phoneNumber, username, code, isLockedOut } = this.state;
        if (code.length !== 6 || isLockedOut) return;

        this.setState({ isSubmitting: true, isErrorMessageDisplayed: false });

        axios({
            method: 'post',
            url: APIs.resetVerifyOTP,
            data: {
                username,
                [validationType === 'EMAIL' ? 'email' : 'phone']: validationType === 'EMAIL' ? email : (phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`),
                code,
            },
            withCredentials: true,
        })
            .then((response) => {
                if (response.status === 200) {
                    this.setState({ step: 'NEW_PASS', isSubmitting: false, otpFailures: 0 });
                } else {
                    this.setState((prevState) => {
                        const newFailures = prevState.otpFailures + 1;
                        if (newFailures >= config.dashboard.resetPassword.wrongOtp.maxWrongOTPAttempts) {
                            this.enforceLockout();
                        }
                        return { otpFailures: newFailures };
                    });
                    this.showError(response.data.message || errorMessages.serverErr);
                }
            })
            .catch((err) => {
                console.error("API Error:", err);
                this.setState((prevState) => {
                    const newFailures = prevState.otpFailures + 1;
                    if (newFailures >= config.dashboard.resetPassword.wrongOtp.maxWrongOTPAttempts) {
                        this.enforceLockout();
                    }
                    return { otpFailures: newFailures };
                });
                const serverMsg = this.extractErrorMessage(err);
                this.showError(serverMsg || errorMessages.serverNoResponse);
            });
    };

    handleNewPassSubmit = () => {
        const { validationType, email, phoneNumber, username, newPassword, confirmPassword } = this.state;

        if (newPassword !== confirmPassword) {
            this.showError("Passwords do not match");
            return;
        }

        this.setState({ isSubmitting: true, isErrorMessageDisplayed: false });

        axios({
            method: 'post',
            url: APIs.resetSetNewPass,
            data: {
                username,
                [validationType === 'EMAIL' ? 'email' : 'phone']: validationType === 'EMAIL' ? email : (phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`),
                newPassword,
            },
            withCredentials: true,
        })
            .then((response) => {
                if (response.status === 200) {
                    this.setState({ step: 'SUCCESS' });
                } else {
                    this.showError(response.data.message || errorMessages.serverErr);
                }
            })
            .catch((err) => {
                console.error("API Error:", err);
                const serverMsg = this.extractErrorMessage(err);
                this.showError(serverMsg || errorMessages.serverNoResponse);
            });
    };

    startResendTimer = () => {
        if (this.timerInterval) clearInterval(this.timerInterval);
        const waitTime = config.dashboard.resetPassword.resendOtp.resendWaitTimeSeconds;
        this.setState({ resendTimer: waitTime });
        this.timerInterval = setInterval(() => {
            this.setState((prevState) => {
                if (prevState.resendTimer <= 1) {
                    clearInterval(this.timerInterval);
                    return { resendTimer: 0 };
                }
                return { resendTimer: prevState.resendTimer - 1 };
            });
        }, 1000);
    };

    handleResendCode = () => {
        const { isSubmitting, resendTimer, resendAttempts, isBanned, isLockedOut } = this.state;
        const maxAttempts = config.dashboard.resetPassword.resendOtp.maxResendAttempts;

        if (isSubmitting || resendTimer > 0 || isBanned || isLockedOut) return;

        const newAttempts = resendAttempts + 1;
        this.setState({ resendAttempts: newAttempts }, () => {
            if (newAttempts >= maxAttempts) {
                this.enforceBan();
            }
        });

        this.handleIdentitySubmit();
    };

    checkSecurityStatus = () => {
        this.checkBanStatus();
        this.checkLockoutStatus();
    };

    checkBanStatus = () => {
        const banTime = localStorage.getItem('passResetOtpBanTime');
        if (banTime) {
            const banDurationMs = config.dashboard.resetPassword.resendOtp.banDurationMinutes * 60 * 1000;
            const timeElapsed = Date.now() - parseInt(banTime);
            const isStillBanned = timeElapsed < banDurationMs;

            if (isStillBanned) {
                this.setState({ isBanned: true });
                const remainingTime = banDurationMs - timeElapsed;
                if (this.banRefreshTimer) clearTimeout(this.banRefreshTimer);
                this.banRefreshTimer = setTimeout(this.checkBanStatus, remainingTime + 1000);
            } else {
                localStorage.removeItem('passResetOtpBanTime');
                this.setState({ isBanned: false, resendAttempts: 0 });
            }
        } else if (this.state.isBanned) {
            this.setState({ isBanned: false, resendAttempts: 0 });
        }
    };

    checkLockoutStatus = () => {
        const lockoutTime = localStorage.getItem('passResetOtpLockoutTime');
        if (lockoutTime) {
            const lockoutDurationMs = config.dashboard.resetPassword.wrongOtp.wrongOTPLockoutMinutes * 60 * 1000;
            const timeElapsed = Date.now() - parseInt(lockoutTime);
            const isStillLocked = timeElapsed < lockoutDurationMs;

            if (isStillLocked) {
                this.setState({ isLockedOut: true });
                const remainingTime = lockoutDurationMs - timeElapsed;
                if (this.lockoutRefreshTimer) clearTimeout(this.lockoutRefreshTimer);
                this.lockoutRefreshTimer = setTimeout(this.checkLockoutStatus, remainingTime + 1000);
            } else {
                localStorage.removeItem('passResetOtpLockoutTime');
                this.setState({ isLockedOut: false, otpFailures: 0 });
            }
        } else if (this.state.isLockedOut) {
            this.setState({ isLockedOut: false, otpFailures: 0 });
        }
    };

    enforceBan = () => {
        localStorage.setItem('passResetOtpBanTime', Date.now().toString());
        this.setState({ isBanned: true }, this.checkBanStatus);
    };

    enforceLockout = () => {
        localStorage.setItem('passResetOtpLockoutTime', Date.now().toString());
        this.setState({ isLockedOut: true }, this.checkLockoutStatus);
    };

    extractErrorMessage = (err) => {
        if (err.response && err.response.data) {
            const data = err.response.data;
            const rawMsg = data.message || data.msg || data.error || data.Error || null;

            if (rawMsg === "Controller not found") {
                return `Username not found.\nPlease check and try again.`;
            }

            if (rawMsg === "Company not found") {
                return `Account or email not found.\nPlease check and try again.`;
            }

            return rawMsg;
        }
        return err.message || null;
    };

    showError = (msg) => {
        this.setState({
            isErrorMessageDisplayed: true,
            currentErrorMessage: msg,
            isSubmitting: false,
        });
    };

    handleKeyDown = (e, action, isValid) => {
        if (e.key === 'Enter' && isValid && !this.state.isSubmitting) {
            action();
        }
    };

    renderInputField = (id, label, type, value, onChange, ref = null, custom = null, containerClass = '', onKeyDown = null) => (
        <div className={`form-group ${containerClass}`}>
            <InputField
                uniqueId={id}
                labelName={label}
                type={type}
                value={value}
                changeHandler={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                reference={ref}
                customElement={custom}
            />
        </div>
    );

    renderStepIdentity = () => {
        const { validationType, email, phoneNumber, username, isSubmitting } = this.state;
        return (
            <div className="fade-in">
                <div className="form-header">
                    <h2>Verify Identity</h2>
                    <p>Provide your details to receive an OTP code</p>
                </div>
                <div className="form-fields">
                    {validationType === 'EMAIL' ? (
                        this.renderInputField(
                            'reset-email',
                            'Email Address',
                            'email',
                            email,
                            (v) => this.setState({ email: v }),
                            null,
                            null,
                            '',
                            (e) => this.handleKeyDown(e, this.handleIdentitySubmit, !!username && !!email)
                        )
                    ) : (
                        <div className="form-group phone-group">
                            <label className="phone-label">Phone Number</label>
                            <PhoneInput
                                country={this.state.countryCode}
                                value={phoneNumber}
                                onChange={(phone) => this.setState({ phoneNumber: phone })}
                                onKeyDown={(e) => this.handleKeyDown(e, this.handleIdentitySubmit, !!username && !!phoneNumber)}
                                inputClass="phone-input-custom"
                                containerClass="phone-container-custom"
                                dropdownClass="phone-dropdown-custom"
                                searchClass="phone-search-custom"
                                enableSearch={true}
                            />
                        </div>
                    )}
                    {this.renderInputField(
                        'reset-username',
                        'Username',
                        'text',
                        username,
                        (v) => this.setState({ username: v }),
                        null,
                        null,
                        '',
                        (e) => this.handleKeyDown(e, this.handleIdentitySubmit, !!username && (validationType === 'EMAIL' ? !!email : !!phoneNumber))
                    )}
                    <div className="wizard-actions">
                        <div className="login-button-container">
                            <Button
                                uniqueId="identity-submit"
                                text="Send Verification Code"
                                showSpinner={isSubmitting}
                                isDisabled={!username || (validationType === 'EMAIL' ? !email : !phoneNumber)}
                                clickHandler={this.handleIdentitySubmit}
                            />
                        </div>
                        <div className="reset-password-container" style={{ justifyContent: 'center' }}>
                            <span className="reset-password-link" onClick={this.props.onCancel}>
                                Back to Login
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderStepOTP = () => {
        const { code, isSubmitting, validationType, resendTimer, resendAttempts, isBanned, isLockedOut } = this.state;
        return (
            <div className="fade-in">
                <div className="form-header">
                    <h2>Check your {validationType === 'EMAIL' ? 'email' : 'phone'}</h2>
                    <p>We've sent a 6-digit code to verify your identity</p>
                </div>
                <div className="form-fields">
                    {this.renderInputField(
                        'otp-code',
                        '6-Digit Code',
                        'text',
                        code,
                        (v) => this.setState({ code: v.slice(0, 6) }),
                        null,
                        null,
                        '',
                        (e) => this.handleKeyDown(e, this.handleOTPSubmit, code.length === 6)
                    )}

                    <div className="resend-container">
                        {isLockedOut ? (
                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                                Too many attempts? Try again in {config.dashboard.resetPassword.wrongOtp.wrongOTPLockoutMinutes} minutes.
                            </span>
                        ) : isBanned || resendAttempts >= config.dashboard.resetPassword.resendOtp.maxResendAttempts ? (
                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                                Too many attempts? Contact <a href={`mailto:${config.dashboard.resetPassword.resendOtp.supportEmail}`} className="text-accent" style={{ textDecoration: 'none', fontWeight: '600' }}>{config.dashboard.resetPassword.resendOtp.supportEmail}</a> or try again later.
                            </span>
                        ) : resendTimer > 0 ? (
                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                                Didn't receive code? Resend in 00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}
                            </span>
                        ) : (
                            <span
                                className="reset-password-link"
                                style={{ fontSize: '0.85rem' }}
                                onClick={this.handleResendCode}
                            >
                                Didn't receive code? Resend Code
                            </span>
                        )}
                    </div>

                    <div className="wizard-actions">
                        <div className="login-button-container">
                            <Button
                                uniqueId="verify-otp-button"
                                text={isSubmitting ? 'Verifying...' : 'Verify Code'}
                                showSpinner={isSubmitting}
                                isDisabled={code.length !== 6 || isSubmitting || isLockedOut}
                                clickHandler={this.handleOTPSubmit}
                            />
                        </div>
                        <div className="reset-password-container" style={{ justifyContent: 'center' }}>
                            <span className="reset-password-link" onClick={() => this.setState({ step: 'IDENTITY' })}>
                                Change Information
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderStepNewPass = () => {
        const { newPassword, confirmPassword, showNewPassword, showConfirmPassword, isSubmitting } = this.state;
        return (
            <div className="fade-in">
                <div className="form-header">
                    <h2>Set New Password</h2>
                    <p>Choose a secure password for your account</p>
                </div>
                <div className="form-fields">
                    {this.renderInputField(
                        'new-password',
                        'New Password',
                        showNewPassword ? 'text' : 'password',
                        newPassword,
                        (v) => {
                            this.setState({ newPassword: v, isErrorMessageDisplayed: v && confirmPassword && v !== confirmPassword, currentErrorMessage: v && confirmPassword && v !== confirmPassword ? "Passwords do not match" : "" });
                        },
                        null,
                        <span className="material-icons" onClick={() => this.setState({ showNewPassword: !showNewPassword })}>
                            {showNewPassword ? 'visibility_off' : 'visibility'}
                        </span>,
                        'input-container password-group',
                        (e) => this.handleKeyDown(e, this.handleNewPassSubmit, !!newPassword && !!confirmPassword && newPassword === confirmPassword)
                    )}
                    {this.renderInputField(
                        'confirm-password',
                        'Confirm Password',
                        showConfirmPassword ? 'text' : 'password',
                        confirmPassword,
                        (v) => {
                            this.setState({ confirmPassword: v, isErrorMessageDisplayed: v && newPassword && v !== newPassword, currentErrorMessage: v && newPassword && v !== newPassword ? "Passwords do not match" : "" });
                        },
                        null,
                        <span className="material-icons" onClick={() => this.setState({ showConfirmPassword: !showConfirmPassword })}>
                            {showConfirmPassword ? 'visibility_off' : 'visibility'}
                        </span>,
                        'input-container password-group',
                        (e) => this.handleKeyDown(e, this.handleNewPassSubmit, !!newPassword && !!confirmPassword && newPassword === confirmPassword)
                    )}
                    <div className="wizard-actions">
                        <div className="login-button-container">
                            <Button
                                uniqueId="newpass-submit"
                                text="Set Password"
                                showSpinner={isSubmitting}
                                isDisabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
                                clickHandler={this.handleNewPassSubmit}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderStepSuccess = () => {
        return (
            <div className="fade-in">
                <div className="form-header" style={{ textAlign: 'center' }}>
                    <div className="success-icon-wrapper" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                        <i className="material-icons text-accent verified-badge-icon">verified</i>
                    </div>
                    <h2>Password Reset Successful!</h2>
                    <p>Your password has been successfully updated. You can now log in with your new password.</p>
                </div>
                <div className="form-fields">
                    <div className="wizard-actions">
                        <div className="login-button-container">
                            <Button
                                uniqueId="success-login"
                                text="Back to Login"
                                clickHandler={this.props.onCancel}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { step, isErrorMessageDisplayed, currentErrorMessage } = this.state;

        if (step === 'DISCOVERY') {
            return <div className="login-form fade-in"><p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Verifying security method...</p></div>;
        }

        return (
            <div className="login-form">
                {step === 'IDENTITY' && this.renderStepIdentity()}
                {step === 'OTP' && this.renderStepOTP()}
                {step === 'NEW_PASS' && this.renderStepNewPass()}
                {step === 'SUCCESS' && this.renderStepSuccess()}

                <div className={`error-card ${isErrorMessageDisplayed ? 'visible' : 'invisible'}`}>
                    <div className="error-content">
                        <i className="material-icons">error_outline</i>
                        <div className="error-text">
                            <span className="error-title">Verification Issue</span>
                            <p>{currentErrorMessage}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ResetPasswordWizard;
